import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, projectCardVariant } from '../../animations/animations';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';

const featuredProjects = [
  {
    title: 'Secure OTA Firmware Updater',
    emoji: '🔐',
    status: 'Production',
    tags: ['Embedded C', 'AES-256-CBC', 'RSA-2048', 'SHA-256', 'AMLOGIC', 'CVITEK'],
    highlight: 'Production OTA system with streaming decryption and cryptographic signature verification - built from scratch in C.',
    challenge: 'The fleet needed a secure, reliable way to update firmware across AMLOGIC and CVITEK platforms in the field. No existing OTA solution handled the platform-specific differences or provided the cryptographic guarantees required for a production deployment.',
    solution: 'How It Was Built:',
    solutionPoints: [
      'Streaming AES-256-CBC decryption - the update never fully lands on disk unverified',
      'RSA-2048 signature verification runs before any file touches the target filesystem',
      'SHA-256-based downgrade protection with a hashed secret override for emergency rollbacks',
      'Pre-flight disk space checks prevent partial writes from leaving the device in a broken state',
      'Post-install handling is platform-specific (AMLOGIC vs CVITEK) for correct reboot sequencing',
    ],
    features: [
      'Communicates with the calling orchestration layer through a structured exit-code protocol (codes 0–7)',
    ],
    impact: 'Zero successful unauthorized firmware installs in production | Cryptographically verified update pipeline | Clean integration with orchestration layer via exit-code protocol',
  },
  {
    title: 'OEM Firmware Reverse Engineering',
    emoji: '🔍',
    status: 'Production',
    tags: ['Ghidra', 'Wireshark', 'Logic Analyzer', 'UART', 'I2C', 'Embedded C', 'ESP32'],
    highlight: 'Reverse engineered a proprietary OEM BTC miner firmware stack with zero vendor documentation and built a compatible replacement in one week.',
    challenge: 'A client needed granular control over their ASIC hardware. The OEM firmware was a black box - no vendor docs, no protocol specs, no source. The only way forward was to figure it out from the hardware signals up.',
    solution: 'Approach:',
    solutionPoints: [
      'Logic Analyzer: Captured and decoded UART and I2C signal traffic between the control board and ASICs',
      'Wireshark: Mapped the TCP/Stratum network control protocol from live traffic captures',
      'Ghidra: Analyzed OEM firmware binary to extract the communication logic and protocol implementation',
      'With the full protocol mapped, built compatible replacement control firmware from scratch on ESP32 using ESP-IDF and FreeRTOS',
    ],
    features: [
      'Full protocol compatibility verified against original hardware before deployment',
      'Replacement firmware delivered and deployed in approximately one week',
    ],
    impact: 'Full protocol compatibility with existing hardware | Replacement firmware delivered on a hard deadline | Client gained granular ASIC control previously impossible with OEM stack',
  },
  {
    title: 'Custom ARM Toolchain & Rootfs',
    emoji: '⚙️',
    status: 'Production',
    tags: ['crosstool-ng', 'Buildroot', 'ARM Cortex-A', 'CVITEK', 'CMake', 'Debian'],
    highlight: 'Built a custom ARM cross-compiler toolchain from scratch for a locked-down platform where no vendor toolchain existed.',
    challenge: 'The target CVITEK platform had a locked bootloader and no vendor-provided toolchain. Standard compilers were ABI-incompatible with the custom rootfs. No off-the-shelf solution worked.',
    solution: 'How It Was Built:',
    solutionPoints: [
      'Built a custom ARM cross-compiler toolchain using crosstool-ng configured for the exact target ABI',
      'Maintained Buildroot-based rootfs configs with full control over kernel and library dependencies',
      'Managed build dependencies with CMake across the firmware and library stack',
      'Packaged the entire dev environment as a .deb - dependency resolution happens automatically on install',
    ],
    features: [
      'ABI-compatible toolchain on a platform that officially had no vendor support',
      'New engineer onboarding cut from ~4 hours to ~10 minutes',
    ],
    impact: '40% reduction in release cycle time via CI/CD integration | New engineer onboarding cut from ~4 hours to ~10 minutes | Full ABI compatibility on a platform that officially had none',
  },
  {
    title: 'Linux Namespace Bootloader Workaround',
    emoji: '🐧',
    status: 'Production',
    tags: ['Linux Namespaces', 'unshare', 'CVITEK', 'Embedded Linux', 'Bash'],
    highlight: 'Ran a full custom firmware stack on a locked bootloader platform - without touching the vendor bootloader - using Linux namespaces.',
    challenge: 'The CVITEK platform had a locked bootloader that could not be replaced or modified. But the custom rootfs and firmware stack needed to run on top of it. There was no supported path.',
    solution: 'How It Works:',
    solutionPoints: [
      'Used Linux unshare to create a new mount namespace on top of the OEM environment',
      'Mounted the custom rootfs inside the namespace - fully isolated from the vendor filesystem',
      'The full firmware stack runs as a namespaced process on top of OEM with no bootloader modifications',
      'OEM bootloader and vendor environment remain completely untouched',
    ],
    features: [
      'Compatible with standard process management (systemd, initd, watchdogs)',
      'Solution generalises to any Linux platform with a locked or unmodifiable bootloader',
    ],
    impact: 'Custom firmware stack deployed on a platform with a locked bootloader | Zero vendor bootloader modifications required | Solution running in production across the fleet',
  },
  {
    title: 'Fleet Telemetry & Performance Benchmarking',
    emoji: '📊',
    status: 'Production',
    tags: ['Python', 'Bash', 'Google Sheets API', 'Embedded Linux', 'Fleet Management'],
    highlight: 'Built a post-deployment telemetry system that benchmarked our firmware efficiency against competing OEM stacks running identical hardware.',
    challenge: 'After deployment, there was no visibility into how the fleet was actually performing. No uptime data, no thermal trends, no way to know if our firmware was more or less efficient than the OEM stack it replaced. Problems only surfaced when hardware failed.',
    solution: 'What It Tracks:',
    solutionPoints: [
      'Live and historical hashrates, power draw, board temperatures, fan RPMs, efficiency ratios',
      'Restart history with fault reasons per device across the full fleet',
      'Automated scripts push fleet data to Google Sheets - team and clients get a live health view without SSH access',
      'Used efficiency data to benchmark our firmware against competing OEM stacks on identical hardware',
    ],
    features: [
      'Identified gaps in thermal management and load distribution vs OEM baseline',
      'Drove firmware-level optimizations based on benchmarking data and validated improvements fleet-wide',
    ],
    impact: 'Fleet visibility went from zero to continuous | Efficiency gaps identified and closed through firmware optimization | Clients got a real-time health dashboard without needing SSH access',
  },
];

const ProjectCard = ({ project, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isEven = index % 2 === 0;

  return (
    <motion.div
      variants={projectCardVariant(isEven)}
      initial="initial"
      whileInView="whileInView"
      viewport={{ once: true }}
      className="card mb-8 overflow-hidden"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <h3 className="text-2xl font-bold text-white flex items-center gap-3">
          <span className="text-3xl">{project.emoji}</span>
          {project.title}
        </h3>
        <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-primary/15 text-primaryLight border border-primary/30 whitespace-nowrap">
          {project.status}
        </span>
      </div>

      {/* Tags - embedded style with border */}
      <div className="flex flex-wrap gap-2 mb-6">
        {project.tags.map((tag, idx) => (
          <span key={idx} className="tag-embedded">{tag}</span>
        ))}
      </div>

      {/* Highlight */}
      <div className="p-4 bg-primary/10 border-l-4 border-primary rounded-r-lg mb-6">
        <p className="text-textPrimary font-medium">{project.highlight}</p>
      </div>

      {/* Expandable Content */}
      <motion.div
        initial={false}
        animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="overflow-hidden"
      >
        <div className="space-y-6">
          {/* Challenge */}
          <div>
            <h4 className="text-lg font-semibold text-primary mb-2">The Challenge:</h4>
            <p className="text-textSecondary leading-relaxed">{project.challenge}</p>
          </div>

          {/* Solution */}
          <div>
            <h4 className="text-lg font-semibold text-primary mb-2">{project.solution}</h4>
            <ul className="space-y-2">
              {project.solutionPoints.map((point, idx) => (
                <li key={idx} className="text-textSecondary flex gap-3">
                  <span className="text-primaryLight mt-1.5 flex-shrink-0">▸</span>
                  <span className="leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Features */}
          {project.features.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-primary mb-2">Key Technical Notes:</h4>
              <ul className="space-y-2">
                {project.features.map((feature, idx) => (
                  <li key={idx} className="text-textSecondary flex gap-3">
                    <span className="text-primaryLight mt-1.5 flex-shrink-0">▸</span>
                    <span className="leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Impact */}
          <div className="p-4 bg-success/10 border-l-4 border-success rounded-r-lg">
            <p className="text-textPrimary">
              <span className="font-semibold text-success">Impact: </span>
              {project.impact}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Expand Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="mt-4 flex items-center gap-2 text-primaryLight hover:text-primary transition-colors font-medium"
      >
        {isExpanded ? (
          <>
            <MdExpandLess size={20} />
            Show Less
          </>
        ) : (
          <>
            <MdExpandMore size={20} />
            View Details
          </>
        )}
      </button>
    </motion.div>
  );
};

const Projects = () => {
  return (
    <section id="projects" className="py-24 px-6 bg-darkSurface/30">
      <div className="max-w-4xl mx-auto">
        <motion.h2
          variants={fadeInUp}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="section-header"
        >
          Featured Projects
        </motion.h2>

        <div className="mt-8">
          {featuredProjects.map((project, index) => (
            <ProjectCard key={index} project={project} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
